import katex from 'katex';
import { Sigma } from 'lucide-react';
import { useMemo } from 'react';

import type { LatexContent, ScriptElement } from '@/lib/schema.types';

interface Props {
  element: ScriptElement;
  /**
   * When `true`, the formula stretches to fill its parent (used by freeform
   * mode). Otherwise it sizes to its intrinsic width with `max-w-full`.
   */
  fill?: boolean;
}

function asLatex(content: unknown): LatexContent {
  if (typeof content === 'string') return { source: content };
  if (content && typeof content === 'object') return content as LatexContent;
  return { source: '' };
}

export function LatexElement({ element, fill }: Props) {
  const data = asLatex(element.content);
  const fontSize = (element.style?.fontSize as number | undefined) ?? 18;
  const color = (element.style?.color as string | undefined) ?? '#1f2937';

  const html = useMemo(() => {
    if (!data.source.trim()) return null;
    try {
      return katex.renderToString(data.source, {
        displayMode: data.displayMode ?? false,
        throwOnError: false,
        output: 'html',
        strict: 'ignore',
      });
    } catch (err) {
      return `<span style="color:#dc2626">${(err as Error).message}</span>`;
    }
  }, [data.source, data.displayMode]);

  if (!html) {
    return (
      <div
        style={fill ? { width: '100%', height: '100%' } : undefined}
        className="flex h-20 w-full flex-col items-center justify-center gap-1 rounded-md border border-dashed border-border-subtle bg-bg-subtle/40 text-fg-subtle"
      >
        <Sigma size={20} />
        <span className="text-[11px] font-medium">Add a TeX expression</span>
      </div>
    );
  }

  return (
    <div
      style={{
        fontSize,
        color,
        ...(fill ? { width: '100%', height: '100%' } : null),
      }}
      className={`katex-host overflow-x-auto leading-none ${
        data.displayMode ? 'flex w-full items-center justify-center text-center' : 'inline-block max-w-full'
      } ${fill ? 'h-full' : ''}`}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
